import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card.jsx';
import Button from './ui/Button.jsx';
import { useToast } from '../hooks/useToast.jsx';
import { Calendar, Copy, RotateCw, Loader2 } from 'lucide-react';

/**
 * Shows the user's (or team's) ICS subscribe URL with copy + rotate.
 *
 * Props:
 *  - title: string (card title)
 *  - description: string (blurb above the URL)
 *  - fetchUrl: () => Promise<{ path, token }>
 *  - rotateUrl: () => Promise<{ path, token }>
 */
export default function CalendarSubscribeCard({ title, description, fetchUrl, rotateUrl }) {
  const toast = useToast();
  const [path, setPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);

  useEffect(() => {
    let alive = true;
    fetchUrl()
      .then((res) => alive && setPath(res.path))
      .catch(() => alive && toast.error('Could not load subscribe URL'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [fetchUrl, toast]);

  const fullUrl = path ? `${window.location.origin}${path}` : '';
  // Outlook (and most calendar clients) refuse to subscribe to non-HTTPS or
  // localhost URLs. Detect that up front so the user isn't left wondering
  // why "nothing is being added" when pasting a dev URL.
  const isReachable = path && /^https:\/\//i.test(fullUrl) && !/localhost|127\.0\.0\.1/i.test(fullUrl);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success('Copied subscribe URL');
    } catch {
      toast.error('Copy failed');
    }
  };

  const rotate = async () => {
    if (!confirm('Rotating will invalidate existing subscriptions. Continue?')) return;
    setRotating(true);
    try {
      const res = await rotateUrl();
      setPath(res.path);
      toast.success('URL rotated');
    } catch {
      toast.error('Rotate failed');
    } finally {
      setRotating(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Calendar className="h-4 w-4" />{title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {description && (
          <p className="text-xs text-muted-foreground leading-relaxed">{description}</p>
        )}
        {loading ? (
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />Loading…
          </div>
        ) : (
          <>
            <div className="rounded border border-border bg-muted/40 px-2 py-1.5 text-[10px] font-mono break-all text-muted-foreground">
              {fullUrl}
            </div>
            <div className="flex gap-1.5">
              <Button type="button" variant="secondary" size="sm" onClick={copy} className="h-7 text-xs">
                <Copy className="h-3 w-3 mr-1" />Copy URL
              </Button>
              <Button type="button" variant="ghost" size="sm" onClick={rotate} disabled={rotating} className="h-7 px-2" title="Rotate URL (invalidates existing subscriptions)">
                {rotating ? <Loader2 className="h-3 w-3 animate-spin" /> : <RotateCw className="h-3 w-3" />}
              </Button>
            </div>
            <p className="text-[10px] text-muted-foreground leading-snug">
              Copy the URL above, then in Outlook: <b>Add calendar → Subscribe from web</b> and paste it. KSA ProHR is the source of truth — Outlook pulls this feed on its own schedule (every ~1–3 hours) and adds, updates, and removes events automatically.
            </p>
            {!isReachable && (
              <p className="text-[10px] leading-snug rounded border border-warning/40 bg-warning/10 px-2 py-1.5 text-warning">
                <b>Heads up:</b> Outlook won't subscribe to this URL — it needs to be public HTTPS. Deploy to Azure Static Web Apps (or run behind a public HTTPS tunnel) and try again from the production URL.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
